/**
 * Stable proportional interleave of two arrays.
 *
 * Walks `a` and `b` together and at each step picks the side that has
 * fallen behind in proportional consumption. The result mixes both
 * sources evenly across the full length, regardless of size mismatch.
 *
 * Example: interleave([A,B,C,D,E], [x,y]) → [A, B, x, C, D, y, E]
 *
 * Deterministic (no randomness) so the order is stable across builds.
 */
export function interleave<T>(a: readonly T[], b: readonly T[]): T[] {
  const out: T[] = [];
  let ai = 0;
  let bi = 0;
  while (ai < a.length || bi < b.length) {
    if (ai >= a.length) out.push(b[bi++]!);
    else if (bi >= b.length) out.push(a[ai++]!);
    // Take whichever side's NEXT pick would keep proportional progress
    // most balanced. Adding 1 looks at where we'd be AFTER pushing.
    else if ((ai + 1) / a.length <= (bi + 1) / b.length) out.push(a[ai++]!);
    else out.push(b[bi++]!);
  }
  return out;
}
